import pick from 'lodash/pick';

import type { ExperimentalQueries } from '../__types__/ExperimentalQueries';
import type { ExperimentalComment } from '../__types__/ExperimentalSchema';

const EMPTY = { nodes: [] };

type CommentData = Pick<ExperimentalComment, 'id' | 'body' | 'author' | 'friendlyLastModified'>;

function toExperimentalComment(
	comment: any,
	replies: { [commentId: string]: CommentData[] },
): ExperimentalComment {
	return {
		...createComment(comment),
		location: {
			hasResolvePermission: Boolean(comment.hasResolvePermission),
			resolveProperties: pick(comment.resolveProperties, [
				'resolvedUser',
				'resolvedFriendlyDate',
				'resolvedByDangling',
			]),
			inlineMarkerRef: comment.markerRef || '',
			inlineText: comment.originalSelection || '',
		},
		replies: {
			// TODO FIXME it looks like `createComment` does not populate `replies` field, which is marked as required in
			// `ExperimentalComment` type. Either `createComment` function needs fixing, or the schema
			// @ts-ignore
			nodes: replies[comment.id],
		},
	};
}

function createComment(comment: any): CommentData {
	return {
		id: comment.id || '',
		body: comment.body || '',
		author: {
			id: comment.authorAccountId || '',
			accountId: comment.authorAccountId || '',
			displayName: comment.authorDisplayName || '',
			profilePicture: {
				path: comment.authorAvatarUrl || '',
			},
		},
		friendlyLastModified: comment.lastModificationDate || '',
	};
}

export const experimentalResolvedInlineComments: ExperimentalQueries['experimentalResolvedInlineComments'] =
	async (_, { contentId }, { network }) => {
		if (!contentId) {
			return EMPTY;
		}

		async function getReplies(commentId: string): Promise<[string, CommentData[]]> {
			let comments = [];
			try {
				comments = await (
					await network.cfetch(`/wiki/rest/inlinecomments/1.0/comments/${commentId}/replies`, {
						method: 'GET',
					})
				).json();
			} catch (e) {}

			return [commentId, comments.map(createComment)];
		}

		// Get all resolved comments.
		let comments;
		try {
			comments = await (
				await network.cfetch(`/wiki/rest/inlinecomments/1.0/comments?containerId=${contentId}`, {
					method: 'GET',
				})
			).json();
		} catch (e) {}

		if (!Array.isArray(comments)) {
			return EMPTY;
		}

		// Get all replies for each comment.
		const replies = (await Promise.all(comments.map((comment) => getReplies(comment.id)))).reduce<{
			[commentId: string]: CommentData[];
		}>((replies, [id, nodes]) => {
			replies[id] = nodes;
			return replies;
		}, {});

		return {
			nodes: comments
				.filter((comment) =>
					Boolean(
						comment.resolveProperties &&
							comment.resolveProperties.resolved &&
							comment.resolveProperties.resolvedTime,
					),
				)
				.sort((a, b) => b.resolveProperties.resolvedTime - a.resolveProperties.resolvedTime)
				.map((comment) => toExperimentalComment(comment, replies)),
		};
	};
